/**
 * ScaffOps™ company (scaffolding business) subscription tiers.
 * Create matching recurring prices in Stripe and set STRIPE_*_PRICE_ID env vars.
 */

export const COMPANY_PLAN_KEYS = ["lite", "studio", "command"] as const;
export type CompanyPlanKey = (typeof COMPANY_PLAN_KEYS)[number];

export type CompanyPlanDefinition = {
  key: CompanyPlanKey;
  name: string;
  purpose: string;
  monthlyGbp: number;
  monthlyFeePence: number;
  features: readonly string[];
  /** Higher tiers inherit lower-tier features in marketing copy */
  highlight?: boolean;
};

export const COMPANY_PLANS: Record<CompanyPlanKey, CompanyPlanDefinition> = {
  lite: {
    key: "lite",
    name: "Lite",
    purpose: "View quotes and respond manually",
    monthlyGbp: 50,
    monthlyFeePence: 5000,
    features: [
      "View quotes sent to your business",
      "Respond to customers manually",
      "Directory profile & reviews",
      "Marketplace job alerts",
    ],
  },
  studio: {
    key: "studio",
    name: "Studio",
    purpose: "Quotes, invoices and customer management",
    monthlyGbp: 100,
    monthlyFeePence: 10000,
    features: [
      "Everything in Lite",
      "Create, edit and send quotes",
      "Invoices & client CRM",
      "Quote sections & line-item library",
    ],
  },
  command: {
    key: "command",
    name: "Command",
    purpose: "Full scaffold operating system",
    monthlyGbp: 200,
    monthlyFeePence: 20000,
    highlight: true,
    features: [
      "Everything in Studio",
      "Sites, compliance & handovers",
      "Team, kit, fleet & gangs",
      "Marketplace quoting & full ops dashboard",
    ],
  },
};

export const COMPANY_TRIAL_DAYS = 14;

/**
 * Switching offer: bill credit equal to 6 months on the plan they choose
 * (Lite £300 · Studio £600 · Command £1,200). Marketing “up to” = Command.
 */
export const SWITCH_CREDIT_MONTHS = 6;
export const SWITCH_CREDIT_MAX_GBP =
  COMPANY_PLANS.command.monthlyGbp * SWITCH_CREDIT_MONTHS;

export function switchCreditGbpForPlan(plan: CompanyPlanKey): number {
  return COMPANY_PLANS[plan].monthlyGbp * SWITCH_CREDIT_MONTHS;
}

/** Short marketing line for headlines / CTAs. */
export const SWITCH_CREDIT_HEADLINE = `Up to £${SWITCH_CREDIT_MAX_GBP.toLocaleString("en-GB")}`;

/** Clarifier used under headlines. */
export const SWITCH_CREDIT_EXPLAINER =
  "Equal to 6 months free on the plan you choose (Lite, Studio or Command).";

/** Lowest published monthly price (for “from £X” marketing). */
export const COMPANY_PLAN_FROM_MONTHLY_GBP = COMPANY_PLANS.lite.monthlyGbp;

export function companyPlanList(): CompanyPlanDefinition[] {
  return COMPANY_PLAN_KEYS.map((k) => COMPANY_PLANS[k]);
}

export type CompanyPlanComparisonCell = boolean | string;

export type CompanyPlanComparisonRow = {
  feature: string;
  lite: CompanyPlanComparisonCell;
  studio: CompanyPlanComparisonCell;
  command: CompanyPlanComparisonCell;
};

/** Feature matrix for marketing comparison tables (homepage + /pricing). */
export function companyPlanComparisonRows(): CompanyPlanComparisonRow[] {
  return [
    {
      feature: "Marketplace job alerts",
      lite: true,
      studio: true,
      command: true,
    },
    {
      feature: "View quotes sent to your business",
      lite: true,
      studio: true,
      command: true,
    },
    {
      feature: "Respond to customers manually",
      lite: true,
      studio: true,
      command: true,
    },
    {
      feature: "Directory profile & reviews",
      lite: true,
      studio: true,
      command: true,
    },
    {
      feature: "Directory listing placement",
      lite: "Standard",
      studio: "Pro",
      command: "Elite",
    },
    {
      feature: "Create, edit & send quotes",
      lite: false,
      studio: true,
      command: true,
    },
    {
      feature: "Invoices & client CRM",
      lite: false,
      studio: true,
      command: true,
    },
    {
      feature: "Quote sections & line-item library",
      lite: false,
      studio: true,
      command: true,
    },
    {
      feature: "Sites, compliance & handovers",
      lite: false,
      studio: false,
      command: true,
    },
    {
      feature: "Team, kit, fleet & gangs",
      lite: false,
      studio: false,
      command: true,
    },
    {
      feature: "Marketplace quoting & full ops dashboard",
      lite: false,
      studio: false,
      command: true,
    },
    {
      feature: "Team members",
      lite: "Up to 3",
      studio: "Up to 10",
      command: "Unlimited",
    },
    {
      feature: "Active sites",
      lite: "Up to 5",
      studio: "Up to 30",
      command: "Unlimited",
    },
  ];
}

/** Map legacy plan slugs and normalise stored values. */
export function normalizeCompanyPlan(plan: string | null | undefined): CompanyPlanKey {
  const p = (plan ?? "").trim().toLowerCase();
  if (p === "lite" || p === "studio" || p === "command") return p;
  if (p === "pro") return "command";
  if (p === "standard") return "studio";
  return "lite";
}

export function parseCompanyPlan(input: unknown): CompanyPlanKey | null {
  if (typeof input !== "string") return null;
  const p = input.trim().toLowerCase();
  if (p === "lite" || p === "studio" || p === "command") return p;
  if (p === "standard") return "studio";
  if (p === "pro") return "command";
  return null;
}

export function companyPlanLabel(plan: string | null | undefined): string {
  return COMPANY_PLANS[normalizeCompanyPlan(plan)].name;
}

export function companyPlanMonthlyGbp(plan: string | null | undefined): number {
  return COMPANY_PLANS[normalizeCompanyPlan(plan)].monthlyGbp;
}

export function formatCompanyPlanPrice(gbp: number): string {
  return `£${gbp.toFixed(0)}`;
}

/** Display price with pence for marketing cards (e.g. £100.00). */
export function formatCompanyPlanPriceDetailed(gbp: number): string {
  return `£${gbp.toFixed(2)}`;
}

/** Annual billing discount vs 12× monthly (homepage + future annual Stripe price). */
export const COMPANY_ANNUAL_DISCOUNT_GBP = 200;

export function companyPlanAnnualGbp(planKey: CompanyPlanKey): number {
  return COMPANY_PLANS[planKey].monthlyGbp * 12 - COMPANY_ANNUAL_DISCOUNT_GBP;
}

export function stripePriceIdForPlan(plan: CompanyPlanKey): string | undefined {
  const envMap: Record<CompanyPlanKey, string | undefined> = {
    lite: process.env.STRIPE_LITE_PRICE_ID ?? process.env.STRIPE_STANDARD_PRICE_ID,
    studio: process.env.STRIPE_STUDIO_PRICE_ID,
    command: process.env.STRIPE_COMMAND_PRICE_ID ?? process.env.STRIPE_PRO_PRICE_ID,
  };
  return envMap[plan]?.trim() || undefined;
}
